import React from "react";
import {
  PNL_COLORS,
  PNL_TEXT_COLORS,
} from "../../../../../constants/constants";
import type { RollupWithTagsAndCountsAndRange } from "../../../../../types/types";
import { camelCase } from "lodash";
import { BiggestDurationRollupType } from "../../trendChartUtils";
import { useGetPageFromPath } from "../../../../../utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { TREND_CHART_CONFIG } from "../../../../../constants/customers";
import { TREND_CHART_BAR_WIDTH } from "../../TrendChartStyles";
import { ONE_MINUTE_OF_MS } from "../../../../../utils/timeUtils";
import styled from "styled-components/macro";
import { BAR_PNL_LOSS_ORDER } from "../trendChartContentUtils";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { useBiggestDurationTrendChartBar } from "utils/customHooks/trendChartHooks";
import { useShouldUseDataDumpsForTrendChartBarstacks } from "utils/customHooks/whichServerHooks";
import { useIsDarkMode } from "store/atoms";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

const BarStackStyles = styled.div<{ $isDarkMode: boolean }>`
  .trendChartBar {
    position: relative;
    transition: height 0.2s ease-in;
    min-width: ${TREND_CHART_BAR_WIDTH}px;
    flex-grow: 1;
  }
  .segmentMinutes {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    &.aboveBar {
      align-items: flex-start;
      position: absolute;
      top: -2.4ch;
      left: 0;
      right: 0;
    }
  }
  .totalMinutes {
    position: absolute;
    font-weight: bold;
    text-shadow:
      0px 0px 4px ${({ $isDarkMode }) => ($isDarkMode ? "black" : "white")},
      0px 0px 4px ${({ $isDarkMode }) => ($isDarkMode ? "black" : "white")}; /* double shadow for increased opacity */
    top: -3ch;
    &.shouldScoochUpABit {
      top: -4ch;
    }
    &.belowLossStack {
      top: unset;
      bottom: -3ch;
    }
    left: 0;
    right: 0;
    transform: scale(1.1);
  }
`;

type StackOfTrendChartBarSegmentsProps = {
  rollupMetric: RollupWithTagsAndCountsAndRange;
  categoriesStrings: string[];
  isProfit: boolean;
  shouldScaleRelativeToBiggestBar: boolean;
};
const StackOfTrendChartBarSegments =
  React.memo<StackOfTrendChartBarSegmentsProps>(
    ({
      rollupMetric,
      categoriesStrings,
      isProfit,
      shouldScaleRelativeToBiggestBar,
    }) => {
      const biggestDurationTrendChartBarRollup =
        useBiggestDurationTrendChartBar();
      const ckey = useCustomerKey();
      const config = TREND_CHART_CONFIG[ckey];
      const [flightConfig] = useFlightConfig();
      const showTrendLine = flightConfig.trendChart.showTrendLine;
      const { isTrendsPage } = useGetPageFromPath();
      const shouldShowAnyNumbers = showTrendLine && !isTrendsPage;
      const showNumbersOnSegments =
        !flightConfig.trendChart.hideNumbersOnSegments;
      const showNumbersOnStacks =
        !flightConfig.trendChart.hideNumbersOnSegmentStacks;
      const shouldCombineWaitingAndUnplanned = Boolean(
        config?.shouldCombineWaitingAndUnplanned
      );

      const categoriesNotIncludedInTotalMinutes =
        config?.categoriesNotIncludedInTotalMinutes || [];

      const totalMinutes =
        categoriesStrings
          // don't include "notScheduled" time in the total minutes of downtime
          .filter(
            category => !categoriesNotIncludedInTotalMinutes.includes(category)
          )
          .reduce(
            (tally, category) =>
              rollupMetric[category] > 0
                ? tally + rollupMetric[category]
                : tally,
            0
          ) / ONE_MINUTE_OF_MS;

      const rollupHeightPcts = categoriesStrings.reduce(
        (acc, category) => {
          const rollupHeightPercent = getRollupHeightPercentNum({
            rollupMetric,
            shouldScaleRelativeToBiggestBar,
            biggestDurationTrendChartBarRollup,
            category,
          });
          return { ...acc, [category]: rollupHeightPercent };
        },
        {} as { [category: string]: number }
      );

      const barSegmentSpecs = getBarSegmentSpecs(
        categoriesStrings,
        shouldCombineWaitingAndUnplanned,
        rollupHeightPcts,
        rollupMetric,
        categoriesNotIncludedInTotalMinutes
      );
      // if the topmost label is above the bar segment, we have to scooch the total minutes label up a bit
      const isTopmostDowntimeLabelAboveBar =
        // use barSegmentSpecs[1] because we skip notScheduled / [0]
        barSegmentSpecs[1]?.isSegmentTooSmallToFitNumber ||
        barSegmentSpecs.some(
          ({ allSegmentsAboveAreTooSmall }) => allSegmentsAboveAreTooSmall
        );

      const shouldUseDataDumpsForTrendChart =
        useShouldUseDataDumpsForTrendChartBarstacks();
      const [isDarkMode] = useIsDarkMode();
      const hideProfitCategories = flightConfig.trendChart.hideProfitCategories;
      return (
        <BarStackStyles
          $isDarkMode={isDarkMode}
          data-server-names={[
            shouldUseDataDumpsForTrendChart
              ? SERVER_NAMES.midgardDataDumpsTrendChart
              : SERVER_NAMES.askrAggregates,
          ]}
          className={`barstackWrapper ${
            isProfit ? "profitWrapper" : "lossWrapper"
          }`}
        >
          {barSegmentSpecs.map(
            (
              {
                category,
                rollupHeightPctDisplay,
                isTopmostDowntimeSegment,
                minutes,
                isSegmentTooSmallToFitNumber,
                shouldSkipCategory,
                fontColor,
              },
              idx
            ) => {
              if (shouldSkipCategory) {
                return null;
              }

              // show a number above the top stack if we're at the topmost segment
              const showNumberAboveTopStack =
                isTopmostDowntimeSegment &&
                // and we're in the top stack
                (isProfit || hideProfitCategories);

              // show a number below the bottom stack if we're at the bottommost segment
              const showNumberBelowBottomStack =
                idx === barSegmentSpecs.length - 1 &&
                // and we're in the bottom stack
                !isProfit &&
                !hideProfitCategories;

              return (
                <div
                  className={`trendChartBar ${category}${
                    rollupHeightPctDisplay === "0%" ? " zeroHeight" : ""
                  }`}
                  key={category}
                  style={{
                    background: PNL_COLORS[camelCase(category)],
                    maxHeight: rollupHeightPctDisplay,
                    height: rollupHeightPctDisplay,
                    ...(isTopmostDowntimeSegment
                      ? { position: "relative" }
                      : {}),
                  }}
                >
                  {shouldShowAnyNumbers ? (
                    <>
                      {/* numbers on segments: */}
                      {showNumbersOnSegments && minutes !== 0 && (
                        <div
                          className={`segmentMinutes${
                            isSegmentTooSmallToFitNumber ? " aboveBar" : ""
                          }`}
                          style={{ color: fontColor }}
                        >
                          {minutes}
                        </div>
                      )}
                      {/* numbers on top of (or below) the entire barstack */}
                      {showNumbersOnStacks && (
                        <>
                          {/* number above the top stack: */}
                          {showNumberAboveTopStack && (
                            <div
                              className={`totalMinutes${
                                isTopmostDowntimeLabelAboveBar
                                  ? " shouldScoochUpABit"
                                  : ""
                              }`}
                            >
                              {Math.round(totalMinutes)}
                            </div>
                          )}
                          {/* number below bottom (usually loss) stack: */}
                          {showNumberBelowBottomStack && (
                            <div className={`totalMinutes belowLossStack`}>
                              {Math.round(totalMinutes)}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  ) : null}
                </div>
              );
            }
          )}
        </BarStackStyles>
      );
    }
  );

export default StackOfTrendChartBarSegments;

const ROLLUP_HEIGHT_PCT_EQUAL_TO_1EM = 4.5;

function getBarSegmentSpecs(
  categoriesStrings: string[],
  shouldCombineWaitingAndUnplanned: boolean,
  rollupHeightPcts: { [category: string]: number },
  rollupMetric: RollupWithTagsAndCountsAndRange,
  categoriesNotIncludedInTotalMinutes: string[]
) {
  const numExcludedCategories = categoriesNotIncludedInTotalMinutes.filter(
    cat => categoriesStrings.includes(cat)
  ).length;

  return categoriesStrings.map((category, idx) => {
    // for <customer>, join the yellow and orange bars together
    const shouldCombineAndIsUnplanned =
      shouldCombineWaitingAndUnplanned && category === "unplanned";
    const shouldSkipCategory =
      shouldCombineWaitingAndUnplanned && category === "waiting";
    const [rollupHeightPct, rollupTimeMs] = shouldCombineAndIsUnplanned
      ? [
          rollupHeightPcts.unplanned + rollupHeightPcts.waiting,
          rollupMetric.unplanned + rollupMetric.waiting,
        ]
      : [rollupHeightPcts[category], rollupMetric[category]];
    const rollupHeightPctDisplay = `${rollupHeightPct}%`;
    const minutes = Math.round((rollupTimeMs || 0) / ONE_MINUTE_OF_MS);
    // if the bar is too small, we move the number up above it
    const isSegmentTooSmallToFitNumber =
      minutes !== 0 && rollupHeightPct < ROLLUP_HEIGHT_PCT_EQUAL_TO_1EM; // percent that matches up to 1em height in px

    const isTopmostDowntimeSegment = idx === numExcludedCategories; // e.g. idx = 2 when we skip the top segments "notScheduled" and "planned"

    // if the number fits inside the segment,
    const categoryOfBackground = !isSegmentTooSmallToFitNumber
      ? // the background colour is that of its own category
        category
      : // otherwise it's bumped up to be in front of the previous category's colour
        // exception: if it's the second-last ("TopmostDowntime") segment, and the next segment is too small to fit a number, the background is "none"
        getAreAllDowntimeSegmentsAboveTooSmallToFitNumbers(
            rollupMetric,
            rollupHeightPcts,
            category,
            categoriesNotIncludedInTotalMinutes
          )
        ? "allSegmentsAboveAreTooSmall"
        : categoriesStrings[idx - 1] || "none";
    // if the colour isn't found, it's going to be on a white background, so use black
    const fontColor = PNL_TEXT_COLORS[categoryOfBackground] || "black";
    return {
      category,
      allSegmentsAboveAreTooSmall:
        categoryOfBackground === "allSegmentsAboveAreTooSmall",
      rollupHeightPctDisplay,
      isTopmostDowntimeSegment,
      minutes,
      isSegmentTooSmallToFitNumber,
      shouldSkipCategory,
      fontColor,
    };
  });
}

function getAreAllDowntimeSegmentsAboveTooSmallToFitNumbers(
  rollupMetric: RollupWithTagsAndCountsAndRange,
  rollupHeightPcts: { [category: string]: number },
  category: string,
  categoriesNotIncludedInTotalMinutes: string[]
) {
  const categoriesOrderedBottomToTop = [
    ...Object.keys(BAR_PNL_LOSS_ORDER),
  ].reverse();
  const categoryRank = BAR_PNL_LOSS_ORDER[category];
  const categoriesAboveThisCategory = categoriesOrderedBottomToTop
    .slice(categoryRank + 1)
    .filter(cat => !categoriesNotIncludedInTotalMinutes.includes(cat));
  return (
    categoriesAboveThisCategory.length > 0 &&
    categoriesAboveThisCategory.every(cat => {
      return getIsAnotherSegmentTooSmallToFitNumber(
        rollupMetric,
        rollupHeightPcts,
        cat
      );
    })
  );
}

// this function checks OTHER segments to see if they're too small (difference is that minutes === 0 returns true)
function getIsAnotherSegmentTooSmallToFitNumber(
  rollupMetric: RollupWithTagsAndCountsAndRange,
  rollupHeightPcts: { [category: string]: number },
  category: string
) {
  const [rollupHeightPct, rollupTimeMs] = [
    rollupHeightPcts[category],
    rollupMetric[category],
  ];
  const minutes = Math.round(rollupTimeMs / ONE_MINUTE_OF_MS);
  return minutes === 0 || rollupHeightPct < ROLLUP_HEIGHT_PCT_EQUAL_TO_1EM; // percent that matches up to 1em height in px
}

function getRollupHeightPercentNum({
  rollupMetric,
  shouldScaleRelativeToBiggestBar,
  biggestDurationTrendChartBarRollup,
  category,
}: {
  rollupMetric: RollupWithTagsAndCountsAndRange;
  shouldScaleRelativeToBiggestBar: boolean;
  biggestDurationTrendChartBarRollup: BiggestDurationRollupType | null;
  category: string;
}) {
  const isEmptyRollup = !rollupMetric.totalDurationMs;
  const isCategoryTimePositive = rollupMetric[category] > 0;

  const heightPctScaledToBiggestBar =
    biggestDurationTrendChartBarRollup?.duration && isCategoryTimePositive
      ? (rollupMetric[category] / biggestDurationTrendChartBarRollup.duration) *
        100
      : 0;

  const heightPctScaledToFullHeight = isCategoryTimePositive
    ? (rollupMetric[category] / rollupMetric.totalDurationMs) * 100
    : 0;

  const rollupHeightPct = // if scaling relative to other bars, we use the biggest bar to scale all bars
    shouldScaleRelativeToBiggestBar &&
    biggestDurationTrendChartBarRollup?.duration
      ? heightPctScaledToBiggestBar
      : // otherwise, all trend chart bars are equal height:
        // use the total duration to scale all equally
        // (segment height is based on the category duration)
        heightPctScaledToFullHeight;

  return isEmptyRollup ? 0 : rollupHeightPct;
}
